import React from "react"
import dayjs from "dayjs"

// components
import { PrimeButton, Select, ValidationMessage } from "components-design-system"
import { Form, Tooltip, Space } from "antd"

// apis
import { postPrsDocumentStatus } from "api/hooks/studies/useSubmissionsApi"
import { useUserSettings } from "api/hooks"

// context
import useAppState from "context/hooks/useAppState"
import useErrors from "api/utils/useErrors"
// styles
import "./DocsTable.scss"

const PRS_DOCUMENT_TYPE_MAP = {
  PROTOCOL_DOC: "Study Protocol",
  SAP_DOC: "Statistical Analysis Plan (SAP)",
  ICF_DOC: "Informed Consent Form (ICF)",
  PROTO_SAP_DOC: "Study Protocol and SAP",
  PROTO_ICF_DOC: "Study Protocol and ICF",
  PROTO_SAP_ICF_DOC: "Study Protocol, SAP and ICF",
}

const PRS_DOCUMENT_DISABLED_MAP = {
  PROTOCOL_DOC: {
    PROTOCOL_DOC: "'Study Protocol'",
    PROTO_SAP_DOC: "'Study Protocol and SAP'",
    PROTO_ICF_DOC: "'Study Protocol and ICF'",
    PROTO_SAP_ICF_DOC: "'Study Protocol, SAP and ICF'",
  },
  SAP_DOC: {
    SAP_DOC: "'Statistical Analysis Plan (SAP)'",
    PROTO_SAP_DOC: "'Study Protocol and SAP'",
    PROTO_SAP_ICF_DOC: "'Study Protocol, SAP and ICF'",
  },
  ICF_DOC: {
    ICF_DOC: "'Informed Consent Form (ICF)'",
    PROTO_ICF_DOC: "'Study Protocol and ICF'",
    PROTO_SAP_ICF_DOC: "'Study Protocol, SAP and ICF'",
  },
  PROTO_SAP_DOC: {
    PROTO_SAP_DOC: "'Study Protocol and SAP'",
    PROTOCOL_DOC: "'Study Protocol'",
    SAP_DOC: "'Statistical Analysis Plan (SAP)'",
    PROTO_SAP_ICF_DOC: "'Study Protocol, SAP and ICF'",
  },
  PROTO_ICF_DOC: {
    PROTO_ICF_DOC: "'Study Protocol and ICF'",
    PROTOCOL_DOC: "'Study Protocol'",
    ICF_DOC: "'Informed Consent Form (ICF)'",
    PROTO_SAP_ICF_DOC: "'Study Protocol, SAP and ICF'",
  },
  PROTO_SAP_ICF_DOC: {
    PROTO_SAP_ICF_DOC: "'Study Protocol, SAP and ICF'",
    PROTOCOL_DOC: "'Study Protocol'",
    ICF_DOC: "'Informed Consent Form (ICF)'",
    SAP_DOC: "'Statistical Analysis Plan (SAP)'",
    PROTO_SAP_DOC: "'Study Protocol and SAP'",
    PROTO_ICF_DOC: "'Study Protocol and ICF'",
  },
}

const DocsTable = ({ authoringId, documentsData, setDocumentsData, eligibleCtgDocuments, prsCredentials }) => {
  const [isFetchingPrsDocs, setIsFetchingPrsDocs] = React.useState(false)
  const [initPrsDocuments, setInitPrsDocuments] = React.useState(true)
  const [selectedFileType, setSelectedFileType] = React.useState(null)
  const [prsDocsData, setPrsDocsData] = React.useState([])
  const { openToastNotification } = useAppState()
  const { data: userSettings } = useUserSettings()

  const { errorHandler } = useErrors()

  const onSyncPrsDocs = async () => {
    setIsFetchingPrsDocs(true)
    openToastNotification("info", "Note", "Retrieving PRS Documents")
    postPrsDocumentStatus(authoringId, prsCredentials)
      .then((resp) => {
        if (Array.isArray(resp?.data)) {
          openToastNotification("success", "Success", "Successfully retrieved PRS Documents")

          setDocumentsData({
            ...documentsData,
            documents_list: resp?.data.map((doc) => {
              return {
                doc_type_key: doc.doctype_key,
                doc_db_id: null,
                prs_doc_id: doc.prs_doc_id,
                doc_savetime: doc.doc_savetime,
                doc_date: doc.doc_date?.replace("Document Date: ", ""),
                doc_title: doc.doc_title,
                do_add: false,
                do_update: false,
                do_delete: false,
              }
            }),
          })
          setPrsDocsData(resp?.data)
          setInitPrsDocuments(false)
        } else if (resp?.response?.data?.error?.msg) {
          openToastNotification("error", "Error", resp.response.data.error.msg)
        } else if (resp?.data?.error?.msg) {
          openToastNotification("error", "Error", resp.data.error.msg)
        }
      })
      .catch((error) => {
        errorHandler({ error })
      })
      .finally(() => {
        setIsFetchingPrsDocs(false)
      })
  }

  const onChangeDocAction = (value, index) => {
    if (!documentsData.documents_list[index].prs_doc_id) {
      documentsData.documents_list.splice(index, 1)
      setDocumentsData({ ...documentsData })
    } else if (value === "delete") {
      documentsData.documents_list[index].do_update = false
      documentsData.documents_list[index].do_delete = true

      // Finding The orignal prs doc to set the date back to what it was before updating:
      const findPRSDoc = prsDocsData.find(
        (prsDoc) => prsDoc.prs_doc_id === documentsData.documents_list[index].prs_doc_id
      )

      documentsData.documents_list[index].doc_date = findPRSDoc.doc_date
      setDocumentsData({ ...documentsData })
    } else if (value === "undo") {
      const found = documentsData.documents_list.filter((doc) => {
        return PRS_DOCUMENT_DISABLED_MAP[documentsData.documents_list[index].doc_type_key].hasOwnProperty(
          doc.doc_type_key
        )
      })

      if (found.length > 1) {
        openToastNotification("error", "Error", "Please select a file with a different type")
      } else {
        documentsData.documents_list[index].do_update = false
        documentsData.documents_list[index].do_delete = false
        setDocumentsData({ ...documentsData })
      }
    }
  }

  const onAddDocument = (value) => {
    const foundDoc = eligibleCtgDocuments.find((doc) => doc.id === value)

    if (foundDoc) {
      let newStagingDoc = {
        doc_type_key: foundDoc.doc_type_key,
        doc_db_id: value,
        prs_doc_id: null,
        doc_date: foundDoc?.document_date,
        doc_savetime: foundDoc?.doc_savetime,
        doc_title: foundDoc.name,
        do_add: true,
        do_update: false,
        do_delete: false,
      }

      documentsData.documents_list.push(newStagingDoc)
      setDocumentsData({ ...documentsData })
    }
    setSelectedFileType(null)
  }

  const onUpdateStaging = (value, index) => {
    if (!value) {
      documentsData.documents_list[index].do_update = false
      documentsData.documents_list[index].do_delete = false
      documentsData.documents_list[index].doc_db_id = null
      const findPRSDoc = prsDocsData.find((doc) => doc.prs_doc_id === documentsData.documents_list[index].prs_doc_id)
      documentsData.documents_list[index].doc_date = findPRSDoc.doc_date?.replace("Document Date: ", "")
    } else {
      documentsData.documents_list[index].do_update = true
      documentsData.documents_list[index].do_delete = false
      documentsData.documents_list[index].doc_db_id = value
      const findReplacementDoc = eligibleCtgDocuments.find((doc) => doc.id === value)
      documentsData.documents_list[index].doc_date = findReplacementDoc.document_date
    }

    setDocumentsData({ ...documentsData })
  }

  const fileTypeOptions = React.useMemo(() => {
    return Object.keys(PRS_DOCUMENT_TYPE_MAP).map((docTypeKey) => {
      const errors = []

      documentsData.documents_list.forEach((stageDoc) => {
        if (PRS_DOCUMENT_DISABLED_MAP[docTypeKey][stageDoc.doc_type_key] && stageDoc.do_delete === false) {
          errors.push({
            doc: stageDoc,
            error: PRS_DOCUMENT_DISABLED_MAP[docTypeKey][stageDoc.doc_type_key],
          })
        }
      })

      let option = {
        value: docTypeKey,
        label: PRS_DOCUMENT_TYPE_MAP[docTypeKey],
        disabled: false,
      }

      if (errors.length > 0) {
        let errorsStrArr = errors.map((error) => error.error)

        option.disabled = true
        option.tooltip = `To add, please delete ${
          errorsStrArr.slice(0, -1).join(", ") + (errorsStrArr.length > 1 ? " and " : "") + errorsStrArr.slice(-1)
        } file type(s)`
      }

      return option
    })
  }, [eligibleCtgDocuments, documentsData])

  const selectFileType = React.useMemo(() => {
    return (
      <Select
        value={selectedFileType}
        options={fileTypeOptions}
        size="small"
        onChange={setSelectedFileType}
        style={{ width: 200 }}
        optionRender={(option) => {
          if (option.data.tooltip) {
            return (
              <Tooltip
                title={option.data.tooltip}
                placement="left"
              >
                <Space>
                  <div>{option.data.label}</div>
                </Space>
              </Tooltip>
            )
          }

          return (
            <Space>
              <div>
                <div>{option.data.label}</div>
                <div>{option.data.type}</div>
              </div>
            </Space>
          )
        }}
      />
    )
  }, [fileTypeOptions, selectedFileType])

  return (
    <div className="submissions-docs-table-container">
      {initPrsDocuments === false && (
        <table>
          <thead>
            <tr>
              <th>Documents</th>
              <th>Update (If applicable)</th>
              <th>Document Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {documentsData.documents_list.map((prsDoc, i) => {
              return (
                <tr
                  key={prsDoc?.prs_doc_id}
                  valign="top"
                >
                  <td className="main-col">
                    <div className="doc-title-container">
                      <div className={`${prsDoc.do_delete ? "delete-doc" : ""} title`}>
                        <span>{prsDoc.doc_title}</span>
                        {prsDoc.prs_doc_id && (
                          <Tooltip title="Document exists in PRS">
                            <span>
                              <i className="fa-regular fa-cloud prs-doc"></i>
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <div className={`${prsDoc.do_delete ? "delete-doc" : ""} subtitle`}>
                        {PRS_DOCUMENT_TYPE_MAP[prsDoc.doc_type_key]}
                      </div>
                    </div>
                  </td>
                  <td className="main-col">
                    <div style={{ paddingRight: 24 }}>
                      {prsDoc?.do_delete && <div className="danger-text">Document is marked for deletion.</div>}
                      {!prsDoc.do_delete && prsDoc.prs_doc_id && (
                        <Select
                          size="small"
                          placeholder="Select Document"
                          style={{ maxWidth: 226 }}
                          options={eligibleCtgDocuments
                            .filter((eligibleDoc) => {
                              return eligibleDoc.doc_type_key === prsDoc.doc_type_key
                            })
                            .map((eligibleDoc) => ({
                              value: eligibleDoc.id,
                              label: eligibleDoc.name,
                              type: eligibleDoc.doc_type_name,
                            }))}
                          optionRender={(option) => {
                            return (
                              <Space>
                                <div>
                                  <div>{option.data.label}</div>
                                  <div>{option.data.type}</div>
                                </div>
                              </Space>
                            )
                          }}
                          onChange={(val) => onUpdateStaging(val, i)}
                        />
                      )}
                    </div>
                  </td>
                  <td style={{ width: 120 }}>
                    {prsDoc?.doc_date ? (
                      dayjs(prsDoc?.doc_date).format(userSettings?.date_format)
                    ) : (
                      <Tooltip title="File submitted to PRS must have a File Date. Please navigate to the Files tab to populate the File Date for the document.">
                        <div className="document-error-container">
                          <span>Document date is required.</span>
                        </div>
                      </Tooltip>
                    )}
                  </td>
                  <td className="secondary-col">
                    <div className="doc-actions">
                      <Tooltip title={prsDoc.do_delete ? "Undo" : "Delete"}>
                        <i
                          className={`fa-light ${
                            prsDoc.do_delete ? "fa-arrow-rotate-left undo-doc-btn" : "fa-trash-xmark delete-doc-btn"
                          } doc-action-btn`}
                          onClick={() => onChangeDocAction(prsDoc.do_delete ? "undo" : "delete", i)}
                        />
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <div className="file-selectors-container">
        <Form.Item
          label={initPrsDocuments ? "To add files for submission, first sync files from PRS." : "Select Document Type"}
        >
          <div className="docs-table-files-actions">
            {initPrsDocuments === false && selectFileType}
            {initPrsDocuments && (
              <PrimeButton
                icon={<i className={`fa-sharp fa-light fa-arrows-rotate`} />}
                loading={isFetchingPrsDocs}
                onClick={onSyncPrsDocs}
                className="prs-docs-status-btn"
              />
            )}
          </div>
        </Form.Item>
        {selectedFileType && (
          <Form.Item label={"Select Document"}>
            <Select
              size="small"
              options={eligibleCtgDocuments?.filter((eligibleDoc) => {
                if (eligibleDoc.doc_type_key === selectedFileType) return true
                return false
              })}
              onChange={onAddDocument}
              style={{ minWidth: 200 }}
              value={null}
            />
          </Form.Item>
        )}
      </div>
      {eligibleCtgDocuments?.length === 0 && (
        <ValidationMessage
          type="warning"
          value={"There are no PRS document types detected in Prime for this study."}
        />
      )}
    </div>
  )
}

export default DocsTable
